// src/pages/web/SundayTool.jsx
import React, { useState, useEffect, useContext } from "react";
import {
	Box,
	Paper,
	Grid,
	Typography,
	IconButton,
	Button,
	TextField,
	Checkbox,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Collapse,
	Skeleton,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tooltip,
	styled,
} from "@mui/material";
import {
	KeyboardArrowRight as ArrowRightIcon,
	KeyboardArrowLeft as ArrowLeftIcon,
	ExpandMore,
	ExpandLess,
	CheckCircle as CheckIcon,
	Autorenew as SpinnerIcon,
	InfoOutlined,
	ErrorOutline as ErrorIcon,
} from "@mui/icons-material";
import { GlobalContext } from "../../GlobalContext";
import { obtenerGrupos, ejecutar, obtenerEstado, obtenerInfoFinal } from "../../utils/api";

const parseApi = (raw) =>
	Object.entries(raw).map(([store, payload]) => {
		const children = Array.isArray(payload)
			? payload.map((name) => ({
					id: `${store}::${name}`,
					label: name,
					orgpaths: [],
					checked: false,
			  }))
			: Object.entries(payload).map(([grpName, paths]) => ({
					id: `${store}::${grpName}`,
					label: grpName.split("/").pop(),
					orgpaths: paths,
					checked: false,
			  }));
		return {
			id: store,
			label: store.replace(/_/g, " "),
			checked: false,
			children,
		};
	});

function toggleBranch(list, setList, storeId, branch) {
	setList((prev) =>
		prev.map((store) => {
			if (store.id !== storeId) return store;

			// Computamos la unión de todos los orgpaths de esta tienda
			const union = [...new Set(store.children.flatMap((c) => c.orgpaths || []))];
			// Identificamos el full‐branch (el que cubre toda la unión)
			const full = store.children.find(
				(c) =>
					c.orgpaths &&
					union.length > 0 &&
					c.orgpaths.length >= union.length &&
					union.every((p) => c.orgpaths.includes(p))
			);

			// Ahora construimos el nuevo arreglo de hijos:
			const newChildren = store.children.map((c) => {
				if (c.id === branch.id) {
					// Alternamos este branch
					return { ...c, checked: !c.checked };
				}
				if (full && branch.id === full.id) {
					// Si marcamos el full branch, desmarcamos todos los demás
					return { ...c, checked: false };
				}
				// En el resto de casos, lo dejamos igual
				return c;
			});

			// Determinamos el nuevo estado del padre:
			// - Si acabo de marcar el full branch, store.checked = true
			// - Si no, store.checked = todos los children.checked
			const parentChecked =
				full && branch.id === full.id && !branch.checked ? true : newChildren.every((c) => c.checked);

			return {
				...store,
				checked: parentChecked,
				children: newChildren,
			};
		})
	);
}

// 1) Definirlo justo arriba de tu componente, fuera de SundayToolE:
function resetFormActions(
	setStatus,
	setSelected,
	setPeriodo,
	setStartDate,
	setEndDate,
	setResult,
	setErrMsg,
	setAvailable,
	token
) {
	setStatus("IDLE");
	setSelected([]);
	setPeriodo("");
	setStartDate("");
	setEndDate("");
	setResult({});
	setErrMsg("");
	if (MOCK_API) {
		setAvailable(parseApi(mockGroups));
	} else {
		obtenerGrupos(token)
			.then((data) => setAvailable(parseApi(data)))
			.catch(() => setErrMsg("Error al recargar grupos"));
	}
}

const formatMonth = (date) => {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, "0");
	return `${y}-${m}`;
};
const getFirstMonday = (year, month) => {
	const d = new Date(year, month, 1);
	while (d.getDay() !== 1) {
		d.setDate(d.getDate() + 1);
	}
	return d;
};
const getMinSelectableMonth = () => {
	const now = new Date();
	const year = now.getFullYear();
	const month = now.getMonth();
	const firstMonday = getFirstMonday(year, month);

	// Si hoy es igual o después del primer lunes, pasamos al mes siguiente
	const effective = now >= firstMonday ? new Date(year, month + 1, 1) : new Date(year, month, 1);

	return formatMonth(effective);
};

const minMonth = getMinSelectableMonth();

// mock vs real
const MOCK_API = process.env.REACT_APP_USE_MOCK === "true";

// mock data
const mockGroups = {
	"FILE 058": [
		"Tienda FILE 058",
		"Tienda FILE 058 - ASEO",
		"Tienda FILE 058 - MULTIFUNCIONAL",
		"Tienda FILE 058 - SUPERVISOR",
	],
	"FILE 123": ["Tienda FILE 123", "Tienda FILE 123 - CAJA", "Tienda FILE 123 - SUPERVISOR"],
};
const mockResult = {
	org_results: {
		"./Inversiones ENEX/.../Multifuncional": {
			success: [
				{
					personnum: "26936767-9",
					name: "ALEJANDRO QUINTERO NAVA",
					start_date: "2025-06-02",
					end_date: "2025-07-06",
					min_sundays: 3,
					current_sundays: 11,
					additional_sundays: 3,
					additional_saturdays: 0,
				},
			],
			errors: [
				{
					error: "Error",
					hora: "00:00:00",
					mensaje: "Error ejecución",
				},
			],
			alerts: [
				{
					personnum: "Alerta",
					name: "00:00:00",
					alerta: "Error ejecución",
				},
			],
		},
		"./Inversiones ENEX/.../Lubricador": {
			success: [],
			errors: [
				{
					error: "Error disponibilidad",
					hora: "00:00:00",
					mensaje: "Have an error when trying to get the availability.",
				},
			],
			alerts: [],
		},
	},
};

const WIDTH = "37.625rem";
const PAD = "1.25rem";
const RADIUS = "1.25rem";
const GAP = "2rem";

const SectionAccordion = styled(Accordion)(({ theme, ownerState }) => ({
	position: "relative",
	borderRadius: "1.875rem",
	overflow: "hidden", // recorta todo al radio

	"&::before": {
		content: '""',
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		height: "2px", // grosor de la línea
		backgroundColor: theme.palette[ownerState.color].main,
	},
	"& .MuiAccordionSummary-root": {
		px: 1, // padding horizontal más chico
		py: 0.5, // padding vertical reducido
		minHeight: 0,
		"&.Mui-expanded": {
			py: 0.5,
		},
		borderTopLeftRadius: "1.875rem", // fuerza radio en la cabecera
		borderTopRightRadius: "1.875rem",
		backgroundColor: "transparent",
	},
	"& .MuiAccordionDetails-root": {
		px: 1,
		py: 0.5,
		borderBottomLeftRadius: "1.875rem", // fuerza radio en el detalle
		borderBottomRightRadius: "1.875rem",
		backgroundColor: "transparent",
	},
}));

const ResultTableWrapper = styled("div")({
	overflowX: "auto",
	borderTop: "1px solid #E0E0E0",
	paddingTop: "0.rem",
});

const BorderWrapper = styled(Box)(({ theme, ownerState }) => ({
	border: `2px solid ${theme.palette[ownerState.color].main}`,
	borderRadius: "1.875rem",
	overflow: "hidden",
}));

// justo arriba de tu función SundayToolE (o dentro de ella, antes del return)
const formatOrgLabel = (org) => {
	const parts = org.split("/");
	if (parts.length <= 3) return org; // si no es muy largo, lo dejamos completo
	const lastThree = parts.slice(-3).join("/");
	return `…/${lastThree}`; // ej. “…/Multifuncional”
};

// DESPUÉS
const normalizeErrors = (errs) => {
	// si no viene nada, devolvemos lista vacía
	if (!errs) return [];

	// si no es array, lo envolvemos en uno
	const list = Array.isArray(errs) ? errs : [errs];

	return list.map((e) => ({
		start: e.startdtm ?? "",
		end: e.enddtm ?? "",
		message: e.message ?? e.mensaje ?? e.error ?? "",
		details: e.trace ?? e.details ?? "",
	}));
};

export default function SundayToolE() {
	const { token } = useContext(GlobalContext);
	const STATUS = { IDLE: "IDLE", RUN: "RUN", OK: "OK", ERR: "ERR" };

	const [status, setStatus] = useState(STATUS.IDLE);
	const [available, setAvailable] = useState([]);
	const [selected, setSelected] = useState([]);
	const [periodo, setPeriodo] = useState("");
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");
	const [result, setResult] = useState({});
	const [errMsg, setErrMsg] = useState("");
	const [expanded, setExpanded] = useState([]);

	useEffect(() => {
		(async () => {
			if (MOCK_API) {
				await new Promise((r) => setTimeout(r, 300));
				setAvailable(parseApi(mockGroups));
			} else {
				try {
					const data = await obtenerGrupos(token);
					setAvailable(parseApi(data));
				} catch {
					setErrMsg("Error al cargar grupos");
				}
			}
		})();
	}, [token]);

	useEffect(() => {
		if (!periodo) return;
		const [y, m] = periodo.split("-").map(Number);
		let first = new Date(y, m - 1, 1);
		while (first.getDay() !== 1) first.setDate(first.getDate() + 1);
		setStartDate(first.toISOString().slice(0, 10));
		let last = new Date(y, m, 0);
		while (last.getDay() !== 0) last.setDate(last.getDate() + 1);
		setEndDate(last.toISOString().slice(0, 10));
	}, [periodo]);

	function toggleStore(list, setList, storeId, selectedOrgpaths, isAvailable) {
		setList((prev) =>
			prev.map((store) => {
				if (store.id !== storeId) return store;

				if (isAvailable) {
					// 1) buscar subgrupo que cubra todos los orgpaths
					const union = [...new Set(store.children.flatMap((c) => c.orgpaths || []))];
					const full = store.children.find(
						(c) =>
							c.orgpaths &&
							union.length > 0 &&
							c.orgpaths.length >= union.length &&
							union.every((p) => c.orgpaths.includes(p))
					);
					if (full) {
						// al marcar tienda, marcamos padre + sólo ese hijo, deshabilitamos siblings
						return {
							...store,
							checked: true,
							children: store.children.map((c) => ({ ...c, checked: c.id === full.id })),
						};
					}
					// 2) si no hay full, validamos solapamiento y alert
					if (union.some((p) => selectedOrgpaths.has(p))) {
						window.alert(
							"No puedes marcar toda la tienda: hay solapamiento de rutas.\n" +
								"Elige el grupo que engloba a los demás."
						);
						return store;
					}
				}

				// 3) toggle padre + todos los hijos normalmente
				const newChecked = !store.checked;
				return {
					...store,
					checked: newChecked,
					children: store.children.map((c) => ({ ...c, checked: newChecked })),
				};
			})
		);
	}

	function moveChecked(from, setFrom, to, setTo) {
		const full = [],
			loose = [];
		from.forEach((store) => {
			if (store.checked) full.push(store);
			else {
				const marked = store.children.filter((b) => b.checked);
				if (marked.length) loose.push({ store, branches: marked });
			}
		});
		if (!full.length && !loose.length) return;

		const newFrom = from
			.filter((s) => !s.checked)
			.map((s) => ({ ...s, children: s.children.filter((b) => !b.checked) }))
			.filter((s) => s.children.length);
		setFrom(newFrom);

		// fusionar en destino sin reemplazo completo
		setTo((prev) => {
			let newTo = [...prev];

			// **full stores**: en vez de sobreescribir, fusión de hijos:
			full.forEach((fs) => {
				const idx = newTo.findIndex((s) => s.id === fs.id);
				const allChildren = fs.children.map((c) => ({ ...c, checked: false }));
				if (idx > -1) {
					const merged = [
						...newTo[idx].children,
						...allChildren.filter((c) => !newTo[idx].children.some((e) => e.id === c.id)),
					];
					newTo[idx] = { ...newTo[idx], children: merged };
				} else {
					newTo.push({ ...fs, checked: false, children: allChildren });
				}
			});

			// **loose branches**: misma lógica que antes
			loose.forEach(({ store, branches }) => {
				const idx = newTo.findIndex((s) => s.id === store.id);
				const newBranches = branches.map((b) => ({ ...b, checked: false }));
				if (idx === -1) {
					newTo.push({ id: store.id, label: store.label, checked: false, children: newBranches });
				} else {
					const merged = [
						...newTo[idx].children,
						...newBranches.filter((nb) => !newTo[idx].children.some((e) => e.id === nb.id)),
					];
					newTo[idx] = { ...newTo[idx], children: merged };
				}
			});

			return newTo;
		});
	}

	function handleMoveToSelected(available, setAvailable, selected, setSelected) {
		// clonar available para manipular
		const clone = available.map((store) => ({
			...store,
			children: store.children.map((c) => ({ ...c })),
		}));

		// 1) identificar stores marcados
		clone.forEach((store) => {
			if (!store.checked) return;
			const allPaths = new Set(store.children.flatMap((c) => c.orgpaths || []));
			// buscar branch que cubre todo el conjunto
			const candidate = store.children.find((c) => {
				const p = c.orgpaths || [];
				return [...allPaths].every((x) => p.includes(x));
			});
			if (candidate) {
				// desmarcar el padre, marcar sólo el candidate
				store.checked = false;
				store.children.forEach((c) => (c.checked = c.id === candidate.id));
			}
		});

		// 2) detectar solapamientos con los ya en selected
		const existing = new Set(selected.flatMap((s) => s.children.flatMap((c) => c.orgpaths || [])));
		// ramas entrantes
		const incoming = [];
		clone.forEach((store) => {
			if (store.checked) incoming.push(...store.children);
			else incoming.push(...store.children.filter((c) => c.checked));
		});
		const dup = incoming
			.flatMap((c) => c.orgpaths || [])
			.find((p, i, arr) => arr.indexOf(p) !== i || existing.has(p));
		if (dup) {
			window.alert(
				"No puedes mover estos grupos porque comparten rutas con los ya seleccionados.\n" +
					"Muévelos individualmente o elige el grupo que los contenga."
			);
			return;
		}

		// 3) aplicar el movimiento
		moveChecked(clone, setAvailable, selected, setSelected);
	}

	const runProcess = async () => {
		if (!selected.length || !startDate || !endDate) return;
		setStatus(STATUS.RUN);
		if (MOCK_API) {
			setTimeout(() => {
				setResult(mockResult);
				setStatus(STATUS.OK);
			}, 1500);
		} else {
			try {
				const automatic_planning_groups = selected.flatMap((store) =>
					store.children.map((child) => child.label)
				);

				const executionId = await ejecutar(
					{ start_date: startDate, end_date: endDate, automatic_planning_groups },
					token
				);

				const poll = async () => {
					const st = await obtenerEstado(executionId, token);
					if (st === "running") return setTimeout(poll, 20000);
					if (st === "completed") {
						const finalData = await obtenerInfoFinal(executionId, token);

						const { org_results = {}, apg_results = {} } = finalData;

						// 1) normalizo los errores de org_results
						const cleanedOrg = Object.fromEntries(
							Object.entries(org_results).map(([grp, data]) => [
								grp,
								{
									...data,
									errors: normalizeErrors(data.errors),
								},
							])
						);
						// 2) empujo los errores globales de apg_results al mismo mapa
						Object.entries(apg_results).forEach(([grp, { errors }]) => {
							cleanedOrg[grp] = {
								success: [],
								errors: normalizeErrors([errors]),
								alerts: [],
							};
						});

						finalData.org_results = cleanedOrg;

						setResult(finalData);
						setStatus(STATUS.OK);
					} else {
						setErrMsg("Ejecución fallida");
						setStatus(STATUS.ERR);
					}
				};
				poll();
			} catch {
				setErrMsg("Error al ejecutar flujo");
				setStatus(STATUS.ERR);
			}
		}
	};

	const toggleExpand = (id) =>
		setExpanded((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
	const minMonth = getMinSelectableMonth();
	// 2) Handler local que llama a la función externa:
	const handleReset = () =>
		resetFormActions(
			setStatus,
			setSelected,
			setPeriodo,
			setStartDate,
			setEndDate,
			setResult,
			setErrMsg,
			setAvailable,
			token
		);

	// Recopila en un Set todas las rutas de los grupos ya seleccionados
	const selectedOrgpaths = new Set(
		selected.flatMap((s) => s.children.flatMap((c) => c.orgpaths || []))
	);

	return (
		<Box
			sx={{
				bgcolor: "#EEEDE4",
				width: "100%",
				height: "100%",
				py: 4,
				px: 3,
				display: "flex",
				justifyContent: "center",
				alignItems: "flex-start",
				overflow: "auto",
			}}
		>
			{status === STATUS.IDLE && (
				<Paper
					elevation={0}
					sx={{
						width: WIDTH,
						maxHeight: "85vh",
						bgcolor: "#fff",
						borderRadius: RADIUS,
						p: PAD,
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						gap: GAP,
					}}
				>
					<Box sx={{ width: "100%" }}>
						<Typography variant="h6" fontWeight={700} textAlign="center" mb={1}>
							Herramienta de domingos
						</Typography>
						<Typography variant="body2">
							Selecciona uno o varios grupos desde <b>lista de grupos</b> para la planificación automática
							de la asignación de domingos.
						</Typography>
					</Box>

					<Grid container alignItems="stretch" sx={{ width: "100%", gap: 2 }}>
						<Grid item sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
							<Typography variant="subtitle2" mb={0.5}>
								Lista de grupos
							</Typography>
							<Paper
								variant="outlined"
								sx={{
									flex: 1,
									display: "flex",
									flexDirection: "column",
									overflow: "hidden",

									maxHeight: "28vh",
								}}
							>
								<List dense sx={{ flex: 1, overflow: "auto" }}>
									{/* isAvailable = true */}
									{available.map((store) => (
										<StoreRow
											key={store.id}
											store={store}
											isAvailable
											selectedOrgpaths={selectedOrgpaths}
											toggleBranch={(c) => toggleBranch(available, setAvailable, store.id, c)}
											toggleStore={() =>
												toggleStore(available, setAvailable, store.id, selectedOrgpaths, true)
											}
										/>
									))}
								</List>
							</Paper>
						</Grid>

						<Grid
							item
							sx={{
								width: 40,
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								gap: 1,
							}}
						>
							<IconButton
								onClick={() => handleMoveToSelected(available, setAvailable, selected, setSelected)}
								sx={{
									width: 40,
									height: 40,
									bgcolor: "#1B0C0C",
									color: "#fff",
									"&:hover": { bgcolor: "#000" },
									borderRadius: "50%",
								}}
							>
								<ArrowRightIcon />
							</IconButton>
							<IconButton
								onClick={() => moveChecked(selected, setSelected, available, setAvailable)}
								sx={{
									width: 40,
									height: 40,
									bgcolor: "#1B0C0C",
									color: "#fff",
									"&:hover": { bgcolor: "#000" },
									borderRadius: "50%",
								}}
							>
								<ArrowLeftIcon />
							</IconButton>
						</Grid>

						<Grid item sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
							<Typography variant="subtitle2" mb={0.5}>
								Grupos seleccionados
							</Typography>
							<Paper
								variant="outlined"
								sx={{
									flex: 1,
									display: "flex",
									flexDirection: "column",
									overflow: "hidden",
									maxHeight: "28vh",
								}}
							>
								<List dense sx={{ flex: 1, overflow: "auto" }}>
									{/* isAvailable = false */}
									{selected.map((store) => (
										<StoreRow
											key={store.id}
											store={store}
											isAvailable={false}
											selectedOrgpaths={selectedOrgpaths}
											toggleBranch={(c) => toggleBranch(selected, setSelected, store.id, c)}
											toggleStore={() => toggleStore(selected, setSelected, store.id, selectedOrgpaths, false)}
										/>
									))}
								</List>
							</Paper>
						</Grid>
					</Grid>

					<Grid container spacing={2} sx={{ width: "100%" }}>
						<Grid item xs={12}>
							<TextField
								type="month"
								label="Período"
								value={periodo}
								fullWidth
								InputLabelProps={{ shrink: true }}
								inputProps={{ min: minMonth }}
								onChange={(e) => setPeriodo(e.target.value)}
								sx={{
									"& .MuiOutlinedInput-root": {
										borderRadius: RADIUS,
										// bordes normales / hover / focused
										"& fieldset": {
											borderColor: "#F2C200",
										},
										"&:hover fieldset": {
											borderColor: "#F2C200",
										},
										"&.Mui-focused fieldset": {
											borderColor: "#F2C200",
										},
									},
								}}
							/>
						</Grid>
						<Grid item xs={6}>
							<TextField label="Inicio" value={startDate} fullWidth disabled />
						</Grid>
						<Grid item xs={6}>
							<TextField label="Fin" value={endDate} fullWidth disabled />
						</Grid>
					</Grid>

					<Button
						fullWidth
						sx={{ bgcolor: "#F2C200", color: "#000", fontWeight: 600, borderRadius: RADIUS }}
						onClick={runProcess}
					>
						Ejecutar
					</Button>
				</Paper>
			)}

			{status === STATUS.RUN && (
				<Grid container spacing={4} sx={{ width: "100%", alignItems: "center" }}>
					<Grid
						item
						xs={12}
						md={4}
						sx={{
							height: "100%",
							display: "flex",
							flexDirection: "column",

							alignItems: "center",
						}}
					>
						<Paper
							sx={{
								width: "100%",
								display: "flex", // 🔑 hace al Paper un flex container
								flexDirection: "column", // colocar icono encima de texto
								justifyContent: "center", // centrar verticalmente
								alignItems: "center", // centrar horizontalmente
								minHeight: "70vh", // tu alto mínimo
								p: 4,
							}}
						>
							<SpinnerIcon sx={{ fontSize: 40, animation: "spin 1s linear infinite" }} />
							<Typography variant="h6" mt={2}>
								Ejecución en proceso
							</Typography>
							<Typography variant="body2" mt={1} textAlign="center">
								Este proceso puede tardar algunos minutos. Una vez finalizado, podrás ver los resultados en
								la parte derecha de esta pantalla.
							</Typography>
						</Paper>
					</Grid>
					<Grid item xs={12} md={8}>
						{[1, 2, 3].map((i) => (
							<Skeleton key={i} variant="rounded" height={140} animation="wave" sx={{ mb: 2 }} />
						))}
					</Grid>
				</Grid>
			)}

			{status === STATUS.ERR && (
				<Grid container spacing={4} sx={{ width: "100%" }}>
					<Grid
						item
						xs={12}
						md={4}
						sx={{
							height: "100%",
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Paper
							elevation={0}
							sx={{
								width: "100%",
								display: "flex",
								flexDirection: "column",

								minHeight: "70vh",
								p: 4,
							}}
						>
							<Box
								sx={{
									flex: 1,
									display: "flex",
									flexDirection: "column",
									justifyContent: "center",
									alignItems: "center",
								}}
							>
								<ErrorIcon sx={{ fontSize: 40, color: "error.main" }} />
								<Typography variant="h6" mt={2}>
									Error en ejecución
								</Typography>
								<Typography variant="body2" mt={1} textAlign="center">
									Hubo un error en la ejecución.
									<br />
									{errMsg}
								</Typography>
							</Box>
							<Button
								fullWidth
								sx={{ bgcolor: "#F2C200", color: "#000", fontWeight: 600, borderRadius: RADIUS }}
								onClick={handleReset}
							>
								Volver a formulario
							</Button>
						</Paper>
					</Grid>

					{/* opcionalmente puedes dejar este grid vacío o poner aquí
        un placeholder, tal como en la vista OK */}
					<Grid item xs={12} md={8} />
				</Grid>
			)}

			{status === STATUS.OK && (
				<ResultView
					result={result}
					expanded={expanded}
					toggleExpand={toggleExpand}
					onReset={handleReset}
				/>
			)}
		</Box>
	);
}

function StoreRow({ store, isAvailable, selectedOrgpaths = new Set(), toggleBranch, toggleStore }) {
	const [open, setOpen] = useState(false);
	// Detectar si existe el full‐branch (como arriba)
	const union = [...new Set(store.children.flatMap((c) => c.orgpaths || []))];
	const full = store.children.find(
		(c) =>
			c.orgpaths &&
			union.length > 0 &&
			c.orgpaths.length >= union.length &&
			union.every((p) => c.orgpaths.includes(p))
	);

	// ¿Algún hijo está seleccionado?
	const someChecked = store.children.some((c) => c.checked);
	// ¿El full‐branch está marcado?
	const fullChecked = full ? full.checked : false;

	// En Available, deshabilito la tienda si:
	//  1) ya hay solapamiento con selectedOrgpaths (como antes), o
	//  2) hay hijos marcados **pero** el full‐branch NO está marcado
	const storeHasOverlap =
		isAvailable &&
		(store.children.some((c) => (c.orgpaths || []).some((p) => selectedOrgpaths.has(p))) ||
			(someChecked && !fullChecked));

	return (
		<>
			<ListItemButton onClick={() => setOpen((o) => !o)}>
				<ListItemIcon>
					<Checkbox
						size="small"
						checked={store.checked}
						disabled={storeHasOverlap}
						onClick={(e) => {
							e.stopPropagation();
							toggleStore();
						}}
					/>
				</ListItemIcon>
				<ListItemText primary={store.label} primaryTypographyProps={{ fontSize: 13 }} />
				{store.children.length > 0 && (
					<IconButton
						size="small"
						onClick={(e) => {
							e.stopPropagation();
							setOpen(!open);
						}}
					>
						{open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
					</IconButton>
				)}
			</ListItemButton>
			<Collapse in={open} sx={{ pl: 4 }} unmountOnExit>
				<List dense>
					{store.children.map((b) => {
						const overlap = (b.orgpaths || []).some((p) => selectedOrgpaths.has(p));
						// si fullChecked y este b NO es el full‐branch, lo deshabilito
						const disabled = isAvailable && (overlap || (fullChecked && b.id !== full.id));

						return (
							<ListItemButton
								key={b.id}
								disabled={disabled}
								sx={{ pl: 1, opacity: disabled ? 0.5 : 1 }}
								onClick={() => !disabled && toggleBranch(b)}
							>
								<ListItemIcon>
									<Checkbox size="small" checked={b.checked} disabled={isAvailable && disabled} />
								</ListItemIcon>
								<ListItemText
									// 4) concatenar tienda + subgrupo
									primary={`${b.label}`}
									primaryTypographyProps={{ fontSize: 12 }}
								/>
							</ListItemButton>
						);
					})}
				</List>
			</Collapse>
		</>
	);
}

function ResultView({ result, expanded, toggleExpand, onReset }) {
	return (
		<Grid container spacing={4} sx={{ width: "100%" }}>
			<Grid
				item
				xs={12}
				md={4}
				sx={{
					height: "100%",
					display: "flex",
					flexDirection: "column",

					alignItems: "center",
				}}
			>
				<Paper
					sx={{
						width: "100%",
						display: "flex", // 🔑 hace al Paper un flex container
						flexDirection: "column", // colocar icono encima de texto
						minHeight: "70vh", // tu alto mínimo
						p: 4,
					}}
				>
					<Box
						sx={{
							flex: 1,
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<CheckIcon sx={{ fontSize: 40, color: "green" }} />
						<Typography variant="h6" mt={2}>
							Ejecución finalizada
						</Typography>
					</Box>
					<Button
						fullWidth
						sx={{ bgcolor: "#F2C200", color: "#000", fontWeight: 600, borderRadius: RADIUS }}
						onClick={onReset}
					>
						Volver a formulario
					</Button>
				</Paper>
			</Grid>
			<Grid item xs={12} md={8}>
				<Box sx={{ maxHeight: "70vh", overflowY: "auto", pr: 1 }}>
					{Object.entries(result.org_results || {}).map(([org, data]) => (
						<Accordion key={org} defaultExpanded sx={{ mb: 2, p: 2, backgroundColor: "#FFF" }}>
							<AccordionSummary sx={{ borderRadius: RADIUS }} expandIcon={<ExpandMore />}>
								<Typography fontWeight={600} noWrap>
									{formatOrgLabel(org)}
								</Typography>
							</AccordionSummary>

							{/* Éxitos */}
							{data.success?.length > 0 && (
								<BorderWrapper ownerState={{ color: "success" }} sx={{ mb: 1 }}>
									<SectionAccordion ownerState={{ color: "success" }} disableGutters>
										<AccordionSummary expandIcon={<ExpandMore />}>
											<Typography color="success.main" fontWeight={600}>
												Éxito ({data.success.length})
											</Typography>
										</AccordionSummary>
										<AccordionDetails>
											<ResultTableWrapper>
												<Table size="small">
													<TableHead size="small">
														<TableRow>
															{[
																"Persona",
																"Nombre",
																"Inicio",
																"Fin",
																"Domingos mínimos",
																"Domingos actuales",
																"Domingos adicionales",
																"Sábados adicionales",
															].map((h, i) => (
																<TableCell key={i} sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
																	{h}
																	{h === "Domingos mínimos" && (
																		<Tooltip title="Domingos mínimos requeridos calculados por la herramienta">
																			<InfoOutlined sx={{ ml: 0.5 }} fontSize="small" />
																		</Tooltip>
																	)}
																	{h === "Domingos actuales" && (
																		<Tooltip title="Domingos actuales asignados">
																			<InfoOutlined sx={{ ml: 0.5 }} fontSize="small" />
																		</Tooltip>
																	)}
																	{h === "Sábados adicionales" && (
																		<Tooltip title="Sábados adicionales asignados según lo estipulado en el art. 38 bis del Código del Trabajo">
																			<InfoOutlined sx={{ ml: 0.5 }} fontSize="small" />
																		</Tooltip>
																	)}
																</TableCell>
															))}
														</TableRow>
													</TableHead>
													<TableBody>
														{data.success.map((r, idx) => (
															<TableRow key={idx}>
																<TableCell>{r.personnum}</TableCell>
																<TableCell>{r.name}</TableCell>
																<TableCell>{r.start_date}</TableCell>
																<TableCell>{r.end_date}</TableCell>
																<TableCell>{r.min_sundays}</TableCell>
																<TableCell>{r.current_sundays}</TableCell>
																<TableCell>{r.additional_sundays}</TableCell>
																<TableCell>{r.additional_saturdays}</TableCell>
															</TableRow>
														))}
													</TableBody>
												</Table>
											</ResultTableWrapper>
										</AccordionDetails>
									</SectionAccordion>
								</BorderWrapper>
							)}

							{/* Errores */}
							{data.errors?.length > 0 && (
								<BorderWrapper ownerState={{ color: "error" }} sx={{ mb: 1 }}>
									<SectionAccordion ownerState={{ color: "error" }} disableGutters>
										<AccordionSummary expandIcon={<ExpandMore />}>
											<Typography color="error.main" fontWeight={600}>
												Errores ({data.errors.length})
											</Typography>
										</AccordionSummary>
										<AccordionDetails>
											<ResultTableWrapper>
												<Table size="small">
													<TableHead>
														<TableRow>
															{["Inicio", "Fin", "Mensaje", "Detalles"].map((title) => (
																<TableCell key={title} sx={{ fontWeight: 600 }}>
																	{title}
																</TableCell>
															))}
														</TableRow>
													</TableHead>
													<TableBody>
														{data.errors.map((e, i) => (
															<TableRow key={i}>
																<TableCell>{e.start}</TableCell>
																<TableCell>{e.end}</TableCell>
																<TableCell>{e.message}</TableCell>
																<TableCell>{e.details}</TableCell>
															</TableRow>
														))}
													</TableBody>
												</Table>
											</ResultTableWrapper>
										</AccordionDetails>
									</SectionAccordion>
								</BorderWrapper>
							)}

							{/* Alertas */}
							{/* Alertas (solo filas con mensajes) */}
							{(() => {
								// filtramos alertas cuyo array de mensajes no esté vacío
								const validAlerts = (data.alerts || []).filter(
									(a) => Array.isArray(a.alerts) && a.alerts.length > 0
								);
								if (!validAlerts.length) return null;

								return (
									<BorderWrapper ownerState={{ color: "warning" }} sx={{ mb: 1 }}>
										<SectionAccordion ownerState={{ color: "warning" }} disableGutters>
											<AccordionSummary expandIcon={<ExpandMore />}>
												<Typography color="warning.main" fontWeight={600}>
													Alertas ({validAlerts.length})
												</Typography>
											</AccordionSummary>
											<AccordionDetails>
												<ResultTableWrapper>
													<Table size="small">
														<TableHead>
															<TableRow>
																<TableCell sx={{ fontWeight: 500 }}>Persona</TableCell>
																<TableCell sx={{ fontWeight: 500 }}>Nombre</TableCell>
																<TableCell sx={{ fontWeight: 500 }}>Alerta</TableCell>
															</TableRow>
														</TableHead>
														<TableBody>
															{validAlerts.map((a, i) => (
																<TableRow key={i}>
																	<TableCell>{a.personnum}</TableCell>
																	<TableCell>{a.name}</TableCell>
																	{/* Unimos los mensajes del array en una sola celda */}
																	<TableCell>{a.alerts.join(", ")}</TableCell>
																</TableRow>
															))}
														</TableBody>
													</Table>
												</ResultTableWrapper>
											</AccordionDetails>
										</SectionAccordion>
									</BorderWrapper>
								);
							})()}
						</Accordion>
					))}
				</Box>
			</Grid>
		</Grid>
	);
}

// global spinner keyframes
export const styleSheet = `@keyframes spin{100%{transform:rotate(360deg)}}`;
